import { Link } from '@tanstack/react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { QueryState } from '../components/page';
import { useOverduePractices } from '../api/hooks';
import type { OverduePractice } from '../api/types';
import {
  ConnectionBadge,
  PracticeAgentsHeader,
  agoText,
  dateText,
  practiceLoadError,
} from './practice-agents';

export const OVERDUE_UNAVAILABLE = 'This Rudi release does not report overdue exports yet.';

/** A calendar day from Rudi, shown without shifting it into another time zone. */
export function dayText(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return day;
  return new Date(year, month - 1, date).toLocaleDateString();
}

export function PracticeAgentsOverduePage() {
  const overdue = useOverduePractices();
  const rows = [...(overdue.data ?? [])].sort((a, b) => b.daysOverdue - a.daysOverdue);

  return (
    <div>
      <PracticeAgentsHeader />
      <QueryState isLoading={overdue.isLoading} error={practiceLoadError(overdue.error)}>
        {overdue.data === null ? (
          <p role="status" className="text-sm text-muted-foreground">
            {OVERDUE_UNAVAILABLE}
          </p>
        ) : (
          <>
            <p className="mb-4 max-w-2xl text-xs text-muted-foreground">
              Practices with no import more than three days after the export day in their export
              plan. Rudi emails the study coordinator one reminder per due date.
            </p>
            <div className="overflow-x-auto rounded-3xl border">
              <Table aria-label="Overdue exports">
                <TableHeader>
                  <TableRow>
                    <TableHead>Practice</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Days overdue</TableHead>
                    <TableHead>Last import</TableHead>
                    <TableHead>Reminder sent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <OverdueRow key={row.deviceId} row={row} />
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                        No export is overdue.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}

function OverdueRow({ row }: { row: OverduePractice }) {
  return (
    <TableRow>
      <TableCell className="min-w-[12rem]">
        <Link
          to="/practice-agents/$deviceId"
          params={{ deviceId: row.deviceId }}
          className="font-medium underline-offset-4 hover:underline"
        >
          {row.practiceName ?? 'Unnamed Practice'}
        </Link>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <ConnectionBadge connection={row.connection} />
          Last seen {agoText(row.lastSeenAt)}
        </div>
      </TableCell>
      <TableCell>
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId: row.tenantId }}
          className="underline-offset-4 hover:underline"
        >
          {row.tenantName ?? 'Unnamed organisation'}
        </Link>
      </TableCell>
      <TableCell>{row.teamName ?? 'No team'}</TableCell>
      <TableCell>{dayText(row.dueOn)}</TableCell>
      <TableCell className="text-right font-medium text-destructive">{row.daysOverdue}</TableCell>
      <TableCell className="text-xs">
        {row.lastImportAt ? dateText(row.lastImportAt) : 'No import yet'}
      </TableCell>
      <TableCell className="text-xs">
        {row.reminderSentAt ? dateText(row.reminderSentAt) : 'Not yet'}
      </TableCell>
    </TableRow>
  );
}
